import { motion } from 'motion/react';
import { InterstitialOutro as InterstitialOutroType } from '../../../types';
import { StickyFooterBar } from '../../../sticky-footer-bar/sticky-footer-bar';
import { NextButton } from '../next-button/button';
import {
  BackgroundAsset,
  FRAME_TRANSITION_DURATION,
  getAnimationType,
  GraphicContent,
  GraphicLottie,
  TextContent,
  useAutoAdvance,
  useFrameNavigation,
} from './interstitial-shared';

type InterstitialOutroProps = {
  interstitial: InterstitialOutroType;
  onNext?: () => void;
  onBack?: () => void;
  isBackDisabled?: boolean;
};

export const InterstitialOutro = ({ interstitial, onNext, onBack, isBackDisabled }: InterstitialOutroProps) => {
  const frames = interstitial.frames;
  const { currentFrameIndex, setCurrentFrameIndex, handleNext, currentFrame } = useFrameNavigation(frames, onNext);

  const showNextButton =
    currentFrame.graphic?.predefinedAnimation !== 'calculating-cancer-risk' &&
    currentFrame.graphic?.predefinedAnimation !== 'sliding-door'; // calculating cancer risk controls its own next button

  // Use shared auto-advance logic
  useAutoAdvance(frames, currentFrameIndex, setCurrentFrameIndex);

  if (frames.length === 0) {
    return null;
  }

  const animationType = getAnimationType(currentFrame.animateIn);

  const lottieUrl =
    currentFrame.graphic?.type === 'lottie' && currentFrame.graphic?.assetUrl ? currentFrame.graphic.assetUrl : null;

  return (
    <div className="interstitial-outro-container">
      <div className="interstitial-outro-background" />

      <BackgroundAsset backgroundAsset={currentFrame.backgroundAsset} classNamePrefix="interstitial-outro" />

      <div
        className="interstitial-outro-overlay"
        style={{
          opacity: currentFrame.backgroundOpacity ?? 0.6,
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
          backdropFilter: 'blur(35px)',
          WebkitBackdropFilter: 'blur(35px)', // Safari support
        }}
      />

      <div
        className="relative z-10 flex h-full w-full flex-col items-center justify-start pt-8"
        style={
          // HACK: the header image of the calculating cancer risk frame needs to extend to the edge of the screen.
          currentFrame.graphic?.predefinedAnimation === 'calculating-cancer-risk' ? { padding: 0 } : undefined
        }
      >
        {/* Lottie at the top */}
        {lottieUrl && (
          <motion.div
            key={`lottie-${currentFrameIndex}`}
            className="interstitial-outro-lottie"
            initial={animationType.initial}
            animate={animationType.animate}
            transition={{
              duration: FRAME_TRANSITION_DURATION,
              ease: 'easeOut',
            }}
            style={{
              willChange: 'transform, opacity',
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              paddingBottom: '1rem',
            }}
          >
            <GraphicLottie srcUrl={lottieUrl} className="interstitial-outro-lottie-graphic w-full" loop />
          </motion.div>
        )}

        <motion.div
          key={currentFrameIndex}
          className={`interstitial-outro-content ${lottieUrl ? 'justify-start px-4' : ''}`}
          initial={animationType.initial}
          animate={animationType.animate}
          transition={{
            duration: FRAME_TRANSITION_DURATION,
            ease: 'easeOut',
          }}
          style={{ willChange: 'transform, opacity' }}
        >
          <TextContent
            frame={currentFrame}
            textType="above"
            classNames="text-white text-lg font-normal text-center max-w-[28rem] leading-[1.4] opacity-90 px-6"
          />
          {!lottieUrl && (
            <GraphicContent
              interstitial={interstitial}
              frame={currentFrame}
              classNamePrefix="interstitial-outro"
              onNext={handleNext}
              onBack={onBack}
              isBackDisabled={isBackDisabled}
            />
          )}
          <TextContent
            frame={currentFrame}
            textType="below"
            classNames="text-white text-lg font-normal text-center max-w-[28rem] leading-[1.4] opacity-90 px-6"
          />
        </motion.div>

        {showNextButton && (
          <StickyFooterBar variant="dark" onBack={onBack} isBackDisabled={isBackDisabled}>
            <NextButton text={currentFrame.ctaText || 'Next'} onNext={handleNext} />
          </StickyFooterBar>
        )}
      </div>
    </div>
  );
};
